import React from 'react';
import { Button, Dropdown, Field, Input, makeStyles, Option, Text, tokens } from '@fluentui/react-components';
import type { BackendCreateInput, BackendObject, BackendUpdateInput, AuthType } from '../../types/trapi';
import { OwnershipSection } from './SharedFields';

const useStyles = makeStyles({
  formActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalM,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
});

export interface BackendFormProps {
  mode: 'create' | 'edit';
  initialData?: BackendObject;
  existingNames?: string[];
  isLoading?: boolean;
  onSubmit: (data: BackendCreateInput | BackendUpdateInput) => void;
  onCancel: () => void;
  formId?: string;
  hideActions?: boolean;
}

type FormData = {
  name: string;
  url: string;
  authType: AuthType;
  backendType: string;
  keyVaultUrl?: string;
  ownership: { owner: string; team: string; contact: string };
  properties: { subscriptionId?: string; resourceGroupName?: string; accountName?: string };
};

type FormErrors = Partial<Record<'name' | 'url' | 'authType' | 'backendType' | 'keyVaultUrl', string>>;

const AUTH_OPTIONS: AuthType[] = ['ManagedIdentity', 'ApiKey', 'None'];
const BACKEND_TYPES = ['AOAI', 'AIFoundry', 'VLLM', 'K8s', 'MLWorkspace'];

const BackendForm: React.FC<BackendFormProps> = ({ mode, initialData, existingNames = [], isLoading, onSubmit, onCancel, formId, hideActions = false }) => {
  const styles = useStyles();
  const [data, setData] = React.useState<FormData>({
    name: '',
    url: '',
    authType: 'ManagedIdentity',
    backendType: 'AOAI',
    keyVaultUrl: '',
    ownership: { owner: '', team: '', contact: '' },
    properties: {},
  });
  const [errors, setErrors] = React.useState<FormErrors>({});

  React.useEffect(() => {
    if (initialData) {
      setData({
        name: initialData.name || '',
        url: initialData.url || '',
        authType: (initialData.authType as AuthType) || 'ManagedIdentity',
        backendType: initialData.backendType || 'AOAI',
        keyVaultUrl: initialData.keyVaultUrl || '',
        ownership: {
          owner: initialData.ownership?.owner || '',
          team: initialData.ownership?.team || '',
          contact: initialData.ownership?.contact || '',
        },
        properties: initialData.properties || {},
      });
    }
  }, [initialData]);

  const validate = () => {
    const e: FormErrors = {};
    if (!data.name.trim()) e.name = 'Name is required';
    else if (mode === 'create' && existingNames.map(n => n.toLowerCase()).includes(data.name.toLowerCase())) e.name = 'Name must be unique';
    if (!data.url.trim()) e.url = 'URL is required';
    if (!data.backendType) e.backendType = 'Backend type is required';
    if (!data.authType) e.authType = 'Auth type is required';
    if (data.authType === 'ApiKey' && !data.keyVaultUrl?.trim()) e.keyVaultUrl = 'Key Vault secret URL is required for ApiKey auth';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!validate()) return;
    const mutablePayload = {
      url: data.url.trim(),
      authType: data.authType,
      backendType: data.backendType,
      keyVaultUrl: data.keyVaultUrl?.trim() || undefined,
      ownership: data.ownership,
      properties: {
        // Coerce to required shape; leave blanks as empty strings
        subscriptionId: data.properties.subscriptionId || '',
        resourceGroupName: data.properties.resourceGroupName || '',
        accountName: data.properties.accountName || '',
      },
    };
    if (mode === 'create') {
      const createPayload: BackendCreateInput = {
        ...mutablePayload,
        name: data.name.trim(),
        ci: 'BACKEND',
      };
      onSubmit(createPayload);
    } else {
      const updatePayload: BackendUpdateInput = mutablePayload;
      onSubmit(updatePayload);
    }
  };

  return (
    <form id={formId} onSubmit={submit} style={{ display: 'grid', gap: 20 }}>
      {/* Basic Information */}
      <section style={{ display: 'grid', gap: 8 }}>
        <Text weight="semibold">Basic Information</Text>
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: '1fr 1fr' }}>
          <Field label="Name" required validationMessage={errors.name}>
            <Input
              value={data.name}
              disabled={mode === 'edit'}
              onChange={(_, d) => setData(p => ({ ...p, name: d.value }))}
            />
          </Field>
          <Field label="Backend Type" required validationMessage={errors.backendType}>
            <Dropdown value={data.backendType} selectedOptions={[data.backendType]} onOptionSelect={(_, d) => d.optionValue && setData(p => ({ ...p, backendType: d.optionValue! }))}>
              {BACKEND_TYPES.map(t => (
                <Option key={t} value={t}>{t}</Option>
              ))}
            </Dropdown>
          </Field>
        </div>
        <Field label="URL" required validationMessage={errors.url}>
          <Input value={data.url} onChange={(_, d) => setData(p => ({ ...p, url: d.value }))} placeholder="https://example.openai.azure.com/openai" />
        </Field>
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: '1fr 1fr' }}>
          <Field label="Auth Type" required validationMessage={errors.authType}>
            <Dropdown value={data.authType} selectedOptions={[data.authType]} onOptionSelect={(_, d) => d.optionValue && setData(p => ({ ...p, authType: d.optionValue as AuthType }))}>
              {AUTH_OPTIONS.map(t => (
                <Option key={t} value={t}>{t}</Option>
              ))}
            </Dropdown>
          </Field>
          {data.authType === 'ApiKey' && (
            <Field label="Key Vault Secret URL" validationMessage={errors.keyVaultUrl}>
              <Input value={data.keyVaultUrl} onChange={(_, d) => setData(p => ({ ...p, keyVaultUrl: d.value }))} placeholder="https://{vault}.vault.azure.net/secrets/{secret-name}" />
            </Field>
          )}
        </div>
      </section>

      {/* Ownership */}
      <OwnershipSection
        data={data.ownership}
        onChange={(ownership) => setData(p => ({ ...p, ownership }))}
      />

      {/* Properties */}
      <section style={{ display: 'grid', gap: 8, paddingTop: 4, borderTop: `1px solid ${tokens.colorNeutralStroke3}` }}>
        <Text weight="semibold">Properties</Text>
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(3, 1fr)' }}>
          <Field label="Subscription Id"><Input value={data.properties.subscriptionId || ''} onChange={(_, d) => setData(p => ({ ...p, properties: { ...p.properties, subscriptionId: d.value } }))} /></Field>
          <Field label="Resource Group"><Input value={data.properties.resourceGroupName || ''} onChange={(_, d) => setData(p => ({ ...p, properties: { ...p.properties, resourceGroupName: d.value } }))} /></Field>
          <Field label="Account Name"><Input value={data.properties.accountName || ''} onChange={(_, d) => setData(p => ({ ...p, properties: { ...p.properties, accountName: d.value } }))} /></Field>
        </div>
      </section>

      {!hideActions && (
        <div className={styles.formActions}>
          <Button appearance="outline" type="button" onClick={onCancel} disabled={!!isLoading}>Cancel</Button>
          <Button appearance="primary" type="submit" disabled={!!isLoading}>{isLoading ? 'Saving…' : mode === 'create' ? 'Create Backend' : 'Update Backend'}</Button>
        </div>
      )}
    </form>
  );
};

export default BackendForm;
